import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, AlertTriangle, CheckCircle, Eye, FileText, CreditCard, Users } from 'lucide-react';
import PageSeo from '../components/PageSeo';

const SafetyGuide = () => {
  useEffect(() => {
    // Analytics only (SEO metadata handled by PageSeo)
    if (typeof gtag !== 'undefined') {
      gtag('event', 'page_view', {
        'page_title': 'Gift Card Safety Guide - Secure Selling Tips',
        'page_location': window.location.href,
        'event_category': 'seo_content'
      });
    }
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-green-50">
      <PageSeo
        title="Gift Card Safety Guide — Sell Securely | CashifyGCMart"
        description="Complete safety guide for selling gift cards online. Learn security tips, avoid scams, and protect yourself. SSL secured platform with verified buyers."
        path="/safety-guide"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white py-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="flex justify-center mb-6">
            <Shield size={64} className="text-white" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-6">
            Gift Card Safety & Security Guide
          </h1>
          <p className="text-xl md:text-2xl opacity-90 mb-8">
            Learn how to sell gift cards safely and avoid common scams
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            <span className="bg-white/20 px-4 py-2 rounded-full">🔒 SSL Secured</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">✅ Verified Buyers</span>
            <span className="bg-white/20 px-4 py-2 rounded-full">🛡️ Fraud Protection</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Quick Safety Checklist */}
        <div className="bg-green-50 border-l-4 border-green-500 p-6 rounded-r-lg mb-12">
          <h2 className="text-2xl font-bold mb-4 text-green-800 flex items-center">
            <CheckCircle className="mr-2" size={24} />
            Quick Safety Checklist
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Verify platform is SSL secured (HTTPS)</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Check business registration and reviews</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Never share card details before agreement</span>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Use secure payment methods only</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Keep all transaction documentation</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle size={16} className="text-green-600" />
                <span className="text-sm">Verify card balance before submitting</span>
              </div>
            </div>
          </div>
        </div>

        {/* Warning Signs Section */}
        <section className="mb-12">
          <div className="flex items-center mb-6">
            <AlertTriangle className="text-red-500 mr-3" size={32} />
            <h2 className="text-3xl font-bold text-gray-800">🚨 Red Flags to Avoid</h2>
          </div>
          
          <div className="space-y-6">
            <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-red-800 mb-3">Immediate Payment Requests</h3>
              <p className="text-gray-700 mb-3">
                <strong>Warning:</strong> Platforms asking for gift card codes or photos before confirming rates and payment terms.
              </p>
              <div className="bg-red-100 p-3 rounded border-l-2 border-red-300">
                <p className="text-sm text-red-700">
                  <strong>Scam Example:</strong> "Send us your card photos now and we'll pay you within minutes!"
                </p>
              </div>
            </div>

            <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-orange-800 mb-3">Too-Good-To-Be-True Rates</h3>
              <p className="text-gray-700 mb-3">
                <strong>Warning:</strong> Offers above 95% of face value or significantly higher than market rates.
              </p>
              <div className="bg-orange-100 p-3 rounded border-l-2 border-orange-300">
                <p className="text-sm text-orange-700">
                  <strong>Reality Check:</strong> Market rates typically range from 70-93%. Rates above 95% are usually scams.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-yellow-800 mb-3">Pressure Tactics</h3>
              <p className="text-gray-700 mb-3">
                <strong>Warning:</strong> "Limited time offer" or "Act now before rates drop" messages.
              </p>
              <div className="bg-yellow-100 p-3 rounded border-l-2 border-yellow-300">
                <p className="text-sm text-yellow-700">
                  <strong>Take Your Time:</strong> Legitimate platforms allow you to compare rates and make informed decisions.
                </p>
              </div>
            </div>

            <div className="bg-purple-50 border-l-4 border-purple-500 p-6 rounded-r-lg">
              <h3 className="text-xl font-bold text-purple-800 mb-3">Unsecure Communication</h3>
              <p className="text-gray-700 mb-3">
                <strong>Warning:</strong> Requests to communicate via personal email, WhatsApp, or social media.
              </p>
              <div className="bg-purple-100 p-3 rounded border-l-2 border-purple-300">
                <p className="text-sm text-purple-700">
                  <strong>Stay Safe:</strong> All transactions should happen through the platform's official website.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Security Features Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800 flex items-center">
            <Lock className="mr-3 text-blue-500" size={32} />
            What Makes a Platform Secure?
          </h2>

          <div className="grid md:grid-cols-2 gap-8">
            <div className="space-y-6">
              <div className="flex items-start space-x-4 bg-blue-50 p-4 rounded-lg">
                <Lock className="text-blue-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">SSL Encryption (HTTPS)</h3>
                  <p className="text-gray-700 text-sm">
                    Look for the padlock icon in your browser. All data transmission should be encrypted.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-green-50 p-4 rounded-lg">
                <Users className="text-green-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">Business Registration</h3>
                  <p className="text-gray-700 text-sm">
                    Legitimate platforms display business registration, address, and contact information.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-purple-50 p-4 rounded-lg">
                <CreditCard className="text-purple-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">Secure Payment Methods</h3>
                  <p className="text-gray-700 text-sm">
                    PayPal, bank transfers, and other traceable payment methods protect both parties.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4 bg-orange-50 p-4 rounded-lg">
                <Eye className="text-orange-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">Transparent Process</h3>
                  <p className="text-gray-700 text-sm">
                    Clear explanation of rates, fees, processing times, and verification procedures.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-indigo-50 p-4 rounded-lg">
                <FileText className="text-indigo-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">Terms of Service</h3>
                  <p className="text-gray-700 text-sm">
                    Comprehensive terms of service and privacy policy that protect user rights.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4 bg-pink-50 p-4 rounded-lg">
                <Shield className="text-pink-500 mt-1 flex-shrink-0" size={24} />
                <div>
                  <h3 className="font-bold text-lg mb-2">Fraud Protection</h3>
                  <p className="text-gray-700 text-sm">
                    Active fraud detection systems and buyer verification processes.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Step-by-Step Safe Selling */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">🛡️ Step-by-Step Safe Selling Process</h2>
          
          <div className="space-y-6">
            <div className="border-l-4 border-blue-500 pl-6 py-4 bg-blue-50 rounded-r-lg">
              <h3 className="text-xl font-bold mb-3 text-blue-800">Step 1: Research the Platform</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Check for SSL certificate (HTTPS URL)</li>
                <li>• Read customer reviews on independent sites</li>
                <li>• Verify business registration and contact information</li>
                <li>• Look for transparent pricing and terms</li>
              </ul>
            </div>

            <div className="border-l-4 border-green-500 pl-6 py-4 bg-green-50 rounded-r-lg">
              <h3 className="text-xl font-bold mb-3 text-green-800">Step 2: Verify Your Card</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Check card balance before submitting</li>
                <li>• Take photos of front and back for your records</li>
                <li>• Note any physical damage or wear</li>
                <li>• Keep purchase receipt if available</li>
              </ul>
            </div>

            <div className="border-l-4 border-purple-500 pl-6 py-4 bg-purple-50 rounded-r-lg">
              <h3 className="text-xl font-bold mb-3 text-purple-800">Step 3: Get Quote Securely</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use the platform's official rate calculator</li>
                <li>• Don't share actual card codes during quote process</li>
                <li>• Compare rates with other platforms</li>
                <li>• Understand all fees and processing times</li>
              </ul>
            </div>

            <div className="border-l-4 border-orange-500 pl-6 py-4 bg-orange-50 rounded-r-lg">
              <h3 className="text-xl font-bold mb-3 text-orange-800">Step 4: Submit Safely</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Only submit through the platform's secure form</li>
                <li>• Ensure you agree to terms before sharing card details</li>
                <li>• Use secure file upload for card images</li>
                <li>• Save confirmation numbers and transaction IDs</li>
              </ul>
            </div>

            <div className="border-l-4 border-pink-500 pl-6 py-4 bg-pink-50 rounded-r-lg">
              <h3 className="text-xl font-bold mb-3 text-pink-800">Step 5: Secure Payment</h3>
              <ul className="space-y-2 text-gray-700">
                <li>• Use only secure payment methods (PayPal, bank transfer)</li>
                <li>• Avoid cash, cryptocurrency, or gift card payments</li>
                <li>• Keep all payment confirmations and receipts</li>
                <li>• Report any payment issues immediately</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Cashifygcmart Security Features */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-8 rounded-lg">
            <h2 className="text-3xl font-bold mb-6">🔒 How Cashifygcmart Keeps You Safe</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <Shield className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Bank-Level SSL Encryption</h3>
                    <p className="text-blue-100 text-sm">All data protected with 256-bit SSL encryption</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Users className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Verified Buyer Network</h3>
                    <p className="text-blue-100 text-sm">230+ pre-screened and verified gift card buyers</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <Eye className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Fraud Detection System</h3>
                    <p className="text-blue-100 text-sm">Advanced AI monitors all transactions for suspicious activity</p>
                  </div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <CreditCard className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Secure Payment Processing</h3>
                    <p className="text-blue-100 text-sm">PayPal and bank transfers with full transaction tracking</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <FileText className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Complete Documentation</h3>
                    <p className="text-blue-100 text-sm">Full transaction history and receipts for your records</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <CheckCircle className="text-green-300 mt-1 flex-shrink-0" size={20} />
                  <div>
                    <h3 className="font-semibold">Money-Back Guarantee</h3>
                    <p className="text-blue-100 text-sm">Full refund if transaction doesn't complete as promised</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Common Scams Section */}
        <section className="mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-800">🎣 Common Gift Card Scams to Avoid</h2>
          
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-red-800 mb-3">1. The "Verification Fee" Scam</h3>
              <p className="text-gray-700 mb-3">
                Scammers ask for an upfront "verification fee" or "processing fee" before they'll buy your gift card.
              </p>
              <div className="bg-red-100 p-3 rounded">
                <p className="text-sm text-red-700">
                  <strong>Reality:</strong> Legitimate platforms never charge fees upfront. They deduct any fees from your payment.
                </p>
              </div>
            </div>

            <div className="bg-orange-50 border border-orange-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-orange-800 mb-3">2. The "Test Transaction" Scam</h3>
              <p className="text-gray-700 mb-3">
                Fraudsters ask you to send a small amount first as a "test" before the main transaction.
              </p>
              <div className="bg-orange-100 p-3 rounded">
                <p className="text-sm text-orange-700">
                  <strong>Reality:</strong> There's no such thing as a "test transaction." Once you send the codes, they're gone.
                </p>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
              <h3 className="text-xl font-bold text-yellow-800 mb-3">3. The "Overpayment" Scam</h3>
              <p className="text-gray-700 mb-3">
                Buyer "accidentally" sends more money than agreed and asks you to send the difference back.
              </p>
              <div className="bg-yellow-100 p-3 rounded">
                <p className="text-sm text-yellow-700">
                  <strong>Reality:</strong> The original payment will be reversed, leaving you out both the gift card and the "refund."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Contacts */}
        <section className="mb-12">
          <div className="bg-gray-100 p-8 rounded-lg border-l-4 border-gray-500">
            <h2 className="text-2xl font-bold mb-4 text-gray-800">🚨 If Something Goes Wrong</h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-bold text-lg mb-3">Report to Authorities:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>FTC:</strong> reportfraud.ftc.gov</li>
                  <li>• <strong>FBI IC3:</strong> ic3.gov</li>
                  <li>• <strong>Local Police:</strong> For significant losses</li>
                </ul>
              </div>
              
              <div>
                <h3 className="font-bold text-lg mb-3">Financial Protection:</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• <strong>PayPal:</strong> File dispute through Resolution Center</li>
                  <li>• <strong>Credit Card:</strong> Contact bank for chargeback</li>
                  <li>• <strong>Documentation:</strong> Keep all records</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <div className="bg-gradient-to-r from-green-600 to-blue-600 text-white p-8 rounded-lg text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Sell Safely?</h2>
          <p className="text-xl mb-6">Join thousands of customers who trust Cashifygcmart for secure gift card exchanges</p>
          <div className="space-y-4 sm:space-y-0 sm:space-x-4 sm:flex sm:justify-center">
            <Link to="/rate-calculator" className="block sm:inline-block bg-white text-green-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors">
              Get Secure Quote
            </Link>
            <Link to="/form-submission" className="block sm:inline-block bg-green-700 text-white px-8 py-3 rounded-lg font-semibold hover:bg-green-800 transition-colors border border-white">
              Start Safe Sale
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SafetyGuide;